/**
 * Client for the delivery / dispatch module (`/deliveries`). Manager routes
 * (list/assign/status) need `delivery.read`/`delivery.write`; the `/me/*`
 * routes are the driver's own workspace (`delivery.deliver`).
 */
import type { Delivery, DeliveryDriver, DeliveryStatusUpdate } from '@erp/shared';

import { api } from './api.js';

export type { Delivery, DeliveryDriver, DeliveryStatus } from '@erp/shared';

export async function listDeliveries(params: { status?: string; driver?: string } = {}): Promise<Delivery[]> {
  return (await api().get<{ items: Delivery[] }>('/deliveries', { params })).data.items;
}

export async function getDelivery(id: string): Promise<Delivery> {
  return (await api().get<Delivery>(`/deliveries/${id}`)).data;
}

export async function listDeliveryDrivers(): Promise<DeliveryDriver[]> {
  return (await api().get<{ items: DeliveryDriver[] }>('/deliveries/drivers')).data.items;
}

export async function createDeliveryFromOrder(salesOrderId: string): Promise<Delivery> {
  return (await api().post<Delivery>('/deliveries/from-order', { salesOrderId })).data;
}

export async function assignDelivery(id: string, driverUserId: string | null): Promise<Delivery> {
  return (await api().put<Delivery>(`/deliveries/${id}/assign`, { driverUserId })).data;
}

export async function setDeliveryStatus(id: string, input: DeliveryStatusUpdate): Promise<Delivery> {
  return (await api().put<Delivery>(`/deliveries/${id}/status`, input)).data;
}

// ---- Driver-scoped (own deliveries) ----
export async function listMyDeliveries(): Promise<Delivery[]> {
  return (await api().get<{ items: Delivery[] }>('/deliveries/me')).data.items;
}

export async function setMyDeliveryStatus(id: string, input: DeliveryStatusUpdate): Promise<Delivery> {
  return (await api().put<Delivery>(`/deliveries/me/${id}/status`, input)).data;
}

/** Upload a proof-of-delivery photo (raw octet-stream, metadata in the query). */
export async function uploadDeliveryProof(id: string, file: File, scope: 'manage' | 'mine' = 'manage'): Promise<Delivery> {
  const body = await file.arrayBuffer();
  const path = scope === 'mine' ? `/deliveries/me/${id}/proof` : `/deliveries/${id}/proof`;
  const r = await api().post<Delivery>(path, body, {
    params: { filename: file.name, mimeType: file.type },
    headers: { 'Content-Type': 'application/octet-stream' },
    transformRequest: [(d) => d],
  });
  return r.data;
}
